import React, { ChangeEvent, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { PatientSex } from 'shared/enums/PatientSex';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import {
  useUpdateClinicPetWithoutPimsMutation,
  useUpdateOneClinicPetMutation,
  SpeciesType,
} from 'shared/types/graphql';
import { PatientWeightUnit } from 'shared/enums/PatientWeightUnit';
import { format } from 'date-fns';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
// TODO: CU-86dvx98k1 Convert CSelect to Select from kibble-ui
import { Box, Flex, TextInput as KTextInput, CSelect, HStack, Icon, Text, Popover } from '@televet/kibble-ui';
import { PopoverPlacement } from 'shared/components/Popover';
import { removeTimezoneFromIsoDateString } from 'shared/utils';

export enum EditablePatientFields {
  Birthday = 'dateOfBirth',
  Breed = 'breed',
  Color = 'color',
  Name = 'name',
  Sex = 'gender',
  Species = 'species',
  Weight = 'weight',
  DeceasedDate = 'dateOfDeath',
}

interface IPopoverDetails {
  databaseField: string;
  displayName: string;
  initialFocusRef:
    | React.MutableRefObject<HTMLInputElement | null>
    | React.MutableRefObject<HTMLSelectElement | null>
    | React.MutableRefObject<HTMLButtonElement | null>
    | undefined;
  body: JSX.Element;
}

interface IOption {
  label: string;
  value: string;
}

interface IEditPatientPopoverProps {
  clinicPetId: string | undefined | null;
  field: EditablePatientFields;
  currentValue: string | number | undefined | null;
  editingEnabled: boolean;
  anchor?: JSX.Element;
  unit?: string | undefined | null;
  placement?: PopoverPlacement | undefined;
  species?: string | null;
}

const EditPatientPopover = ({
  clinicPetId,
  field,
  currentValue,
  editingEnabled,
  anchor,
  unit,
  species,
  placement = PopoverPlacement.Auto,
}: IEditPatientPopoverProps): JSX.Element => {
  const { toastMessagePop } = useCustomToast();
  const { isMobile } = useResolutionProvider();
  const inputFieldRef = useRef<HTMLInputElement | null>(null);
  const selectFieldRef = useRef<HTMLSelectElement | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const [newBirthdate, setNewBirthdate] = useState<Date | undefined>();
  const [newDeceasedDate, setNewDeceasedDate] = useState<Date | undefined>();
  const [selectedValue, setSelectedValue] = useState<string>('');

  const [updateClinicPetWithoutPims] = useUpdateClinicPetWithoutPimsMutation({
    refetchQueries: ['getSidePanelPetProfile'],
  });
  const [updateClinicPet] = useUpdateOneClinicPetMutation({
    refetchQueries: ['getSidePanelPetProfile'],
  });

  const dropdownOptionValues = useMemo((): IOption[] => {
    let optionsArray: IOption[] = [{ label: '', value: '' }];
    switch (field) {
      case EditablePatientFields.Sex:
        optionsArray.push(...Object.entries(PatientSex).map(([key, value]) => ({ label: value, value: key })));
        break;
      case EditablePatientFields.Species:
        optionsArray.push(...Object.entries(SpeciesType).map(([key, value]) => ({ label: value, value: key })));
        break;
      case EditablePatientFields.Weight:
        optionsArray = [...Object.values(PatientWeightUnit).map((value) => ({ label: value, value: value }))];
        break;
    }
    return optionsArray;
  }, [field]);

  const displayValue = useMemo((): string => {
    if (!currentValue) return '- -';

    switch (field) {
      case EditablePatientFields.Birthday:
      case EditablePatientFields.DeceasedDate:
        return currentValue && typeof currentValue === 'string'
          ? format(removeTimezoneFromIsoDateString(currentValue), 'MM/dd/yyyy')
          : '- -';
      case EditablePatientFields.Weight:
        return currentValue > 0 && unit ? `${currentValue || '- -'} ${unit?.toLowerCase() + '.'}` : '- -';
      case EditablePatientFields.Species:
        return !species || species === '' ? '- -' : currentValue === SpeciesType.Other ? species : `${currentValue}`;
      default:
        return `${currentValue}`;
    }
  }, [currentValue, field, species, unit]);

  const isExistingSpecies = useMemo((): boolean => {
    return currentValue !== SpeciesType.Other;
  }, [currentValue]);

  const showOtherInput = useMemo((): boolean => {
    return (!isExistingSpecies && !selectedValue) || selectedValue === SpeciesType.Other;
  }, [isExistingSpecies, selectedValue]);

  const popoverDetails = useMemo((): IPopoverDetails => {
    const TextInput = ({ ...rest }): JSX.Element => (
      <KTextInput
        type="text"
        size="sm"
        placeholder="Please specify"
        defaultValue={field === EditablePatientFields.Species && isExistingSpecies ? '' : species || ''}
        ref={inputFieldRef}
        {...rest}
      />
    );

    const SelectInput = ({ defaultValueOverride }: { defaultValueOverride?: string | null | number }): JSX.Element => (
      <CSelect
        ref={selectFieldRef}
        defaultValue={selectedValue || defaultValueOverride || currentValue || ''}
        h={8}
        onChange={(e): void => setSelectedValue(e.target.value)}
      >
        {dropdownOptionValues.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </CSelect>
    );

    switch (field) {
      case EditablePatientFields.Birthday:
        const defaultBirthDate =
          currentValue && typeof currentValue === 'string' ? removeTimezoneFromIsoDateString(currentValue) : new Date();
        const formattedBirthDate = format(defaultBirthDate, 'yyyy-MM-dd');
        return {
          databaseField: EditablePatientFields.Birthday,
          displayName: 'Date of Birth',
          initialFocusRef: cancelButtonRef,
          body: (
            <TextInput
              type="date"
              defaultValue={formattedBirthDate}
              style={{ padding: '0 16px', cursor: 'pointer' }}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                setNewBirthdate(new Date(e.target.value));
              }}
            />
          ),
        };
      case EditablePatientFields.Breed:
        return {
          databaseField: EditablePatientFields.Breed,
          displayName: 'Breed',
          initialFocusRef: inputFieldRef,
          body: <TextInput />,
        };
      case EditablePatientFields.Color:
        return {
          databaseField: EditablePatientFields.Color,
          displayName: 'Color',
          initialFocusRef: inputFieldRef,
          body: <TextInput />,
        };
      case EditablePatientFields.Name:
        return {
          databaseField: EditablePatientFields.Name,
          displayName: 'Name',
          initialFocusRef: inputFieldRef,
          body: <TextInput />,
        };
      case EditablePatientFields.Sex:
        return {
          databaseField: EditablePatientFields.Sex,
          displayName: 'Sex',
          initialFocusRef: selectFieldRef,
          body: <SelectInput />,
        };
      case EditablePatientFields.Species:
        return {
          databaseField: EditablePatientFields.Species,
          displayName: 'Species',
          initialFocusRef: selectFieldRef,
          body: (
            <Flex flexDirection="column">
              <SelectInput defaultValueOverride={isExistingSpecies ? currentValue : SpeciesType.Other} />
              {showOtherInput && <TextInput mt={2} />}
            </Flex>
          ),
        };
      case EditablePatientFields.Weight:
        return {
          databaseField: EditablePatientFields.Weight,
          displayName: 'Weight',
          initialFocusRef: inputFieldRef,
          body: (
            <Flex>
              <TextInput type="number" size="sm" defaultValue={currentValue || ''} ref={inputFieldRef} />
              <Box w="100px" mx={2} h={8}>
                <SelectInput defaultValueOverride={unit || ''} />
              </Box>
            </Flex>
          ),
        };
      case EditablePatientFields.DeceasedDate:
        const defaultDeceasedDate =
          currentValue && typeof currentValue === 'string' ? removeTimezoneFromIsoDateString(currentValue) : new Date();
        const formattedDeceasedDate = format(defaultDeceasedDate, 'yyyy-MM-dd');

        return {
          databaseField: EditablePatientFields.DeceasedDate,
          displayName: 'Deceased Date',
          initialFocusRef: cancelButtonRef,
          body: (
            <TextInput
              type="date"
              defaultValue={formattedDeceasedDate}
              style={{ padding: '0 16px', cursor: 'pointer' }}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                setNewDeceasedDate(new Date(e.target.value));
              }}
            />
          ),
        };
    }
  }, [currentValue, dropdownOptionValues, field, species, isExistingSpecies, showOtherInput, unit, selectedValue]);

  const { databaseField, initialFocusRef, body, displayName }: IPopoverDetails = popoverDetails;

  const updatePatientField = async (): Promise<void> => {
    const refValue = initialFocusRef?.current?.value;

    const newValue = (): string | number | Date | undefined | null => {
      switch (field) {
        case EditablePatientFields.Birthday:
          return newBirthdate;
        case EditablePatientFields.Weight:
          return refValue && Number(refValue) ? parseFloat(refValue) : null;
        case EditablePatientFields.Species:
          return refValue === SpeciesType.Other ? inputFieldRef?.current?.value || SpeciesType.Other : refValue;
        default:
          return refValue;
      }
    };

    if (!clinicPetId) {
      console.warn('No pet ID provided');
      return;
    }

    const data = {
      [databaseField]: newValue(),
    };

    if (field === EditablePatientFields.Weight && selectFieldRef?.current?.value) {
      data['weightUnit'] = selectFieldRef.current.value;
    }

    if (field === EditablePatientFields.DeceasedDate) {
      try {
        await updateClinicPet({
          variables: {
            where: {
              id: clinicPetId,
            },
            data: {
              dateOfDeath: newDeceasedDate,
            },
          },
        });
      } catch (e) {
        console.error('Error updating clinic pet', e);
        Sentry.captureException(e);
        toastMessagePop(`An error occurred when updating the patient.`, STATUSES.error, 10000);
      }
    } else {
      try {
        await updateClinicPetWithoutPims({
          variables: {
            where: {
              id: clinicPetId,
            },
            data,
          },
        });
        toastMessagePop();
      } catch (e) {
        console.error('Error updating clinic pet', e);
        Sentry.captureException(e);
        toastMessagePop(
          `An error occurred when updating the patient. The patient may have been synced with the PIMS, in which case it is no longer editable in Flow. If the patient hasn't synced, please reload the page and try again. If they have been synced, please edit the patient from the PIMS.`,
          STATUSES.error,
          10000,
        );
      }
    }
  };

  return editingEnabled ? (
    <Popover
      isLazy
      autoFocus={true}
      variant="default"
      headerProps={{
        children: `Edit Patient ${displayName}`,
        hideCloseButton: true,
      }}
      footerProps={{
        submitText: 'Save',
        onSubmit: async (): Promise<void> => {
          await updatePatientField();
        },
      }}
      anchor={
        <Box
          _hover={{ bg: 'background.alpha.hover' }}
          borderRadius="sm"
          p={1}
          ml={field !== EditablePatientFields.DeceasedDate && field !== EditablePatientFields.Name ? '-4px' : '0px'}
          cursor="pointer"
          display="inline-block"
        >
          <HStack spacing={1}>
            {anchor ? (
              anchor
            ) : (
              <Text
                fontSize={field === EditablePatientFields.Name ? '24px' : '14px'}
                fontWeight={field === EditablePatientFields.Name ? 'semibold' : 'regular'}
                noOfLines={1}
                isTruncated
              >
                {displayValue}
              </Text>
            )}
            <HStack h={4}>
              <Icon name="pen" size="sm" />
            </HStack>
          </HStack>
        </Box>
      }
      placement={placement}
      initialFocusRef={initialFocusRef}
      returnFocusOnClose={false}
      closeOnBlur={!isMobile}
      size="md"
    >
      <Box mt={2}>{body}</Box>
    </Popover>
  ) : anchor ? (
    anchor
  ) : (
    <Text
      fontSize={field === EditablePatientFields.Name ? '24px' : '14px'}
      fontWeight={field === EditablePatientFields.Name ? 'semibold' : 'regular'}
      noOfLines={1}
      isTruncated
    >
      {displayValue}
    </Text>
  );
};

export default EditPatientPopover;
